import { Capacitor } from "@capacitor/core";
import { useMediaQuery, useTheme } from "@mui/material";
import { default as decode } from "jwt-decode";
// Use direct imports to avoid circular dependency with model/index.ts
import type { JWT, TokenResponse } from "model/appTypes";
import { useEffect, useState } from "react";
import { useAppStore } from "stores";

// Cache the native platform check at module load time
// This is evaluated once when the module is loaded, before any React renders
const IS_NATIVE_PLATFORM = Capacitor.isNativePlatform();

/**
 * Custom hook that returns true if the app is running on a mobile device.
 * On native Capacitor platforms, this returns true immediately without waiting
 * for media query evaluation, preventing flash of desktop UI.
 */
export function useIsMobile(): boolean {
  const theme = useTheme();

  // Always call the hook to maintain consistent hook ordering
  const mediaQueryResult = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true,
    defaultMatches: IS_NATIVE_PLATFORM,
  });

  // On native platforms, always return true regardless of media query result
  if (IS_NATIVE_PLATFORM) {
    return true;
  }

  return mediaQueryResult;
}

export type UserType = "farmer" | "corporate";

/**
 * Custom hook that returns the current user type.
 * Corporate users have corporations, farmers have farms.
 */
export function useUserType(): UserType {
  const user = useAppStore((state) => state.user);
  const hasCorporations = !!(
    user?.corporations?.length && user.corporations.length > 0
  );
  return hasCorporations ? "corporate" : "farmer";
}

export function useHtmlElementSize(container?: HTMLElement | null) {
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!container) {
      setHeight(0);
      setWidth(0);
      return;
    }

    const updateSize = () => {
      // On some browsers (e.g. Chrome 100+ on Pixel 6) the dimensions
      // can be fractional. We never want that behavior to begin with!
      setHeight(Math.round(container.offsetHeight));
      setWidth(Math.round(container.offsetWidth));
    };

    // Initial measurement
    updateSize();

    // Use ResizeObserver to watch container size changes directly
    // This catches layout shifts, UI overlays, and other changes that don't trigger window resize
    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });

    resizeObserver.observe(container);

    // Fallback: also listen to window resize for cases where ResizeObserver might miss something
    window.addEventListener("resize", updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, [container]);

  return [height, width];
}

let redirectTimeout: ReturnType<typeof setTimeout> | undefined;

export function useTokenRefresh() {
  useEffect(() => {
    const refreshToken = async () => {
      const token = window.localStorage.getItem("jwt");
      if (token) {
        const jwtDecoded = decode<JWT>(token);
        const now = Date.now() / 1000;
        const exp = jwtDecoded.exp;
        const dt = exp - now;

        // only refresh if more than 10s and less than 30min are left in session
        if (dt < 30 * 60 && dt > 10) {
          console.log(
            "Fetching token from API endpoint:",
            import.meta.env.VITE_API_ENDPOINT
          );
          const response = await fetch(
            `${import.meta.env.VITE_API_ENDPOINT}/v1/tokens/`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
              },
            }
          );
          const newToken = ((await response.json()) as TokenResponse).token;
          if (newToken) {
            window.localStorage.setItem("jwt", newToken);
          }
        }

        // TODO: there might be a case when user opened the app with a valid jwt token, was redirected and the token expired. Then user would be redirected back to the login screen
      }

      setTimeout(refreshToken, 60000);
    };
    void refreshToken();
  }, []);
}

export function getRedirectUrlWithToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  if (params.has("redirect")) {
    const redirect = params.get("redirect");
    const token = window.localStorage.getItem("jwt");
    if (redirect) {
      return injectTokenInUrl(redirect, token)?.replace(/\/$/, ""); // inject token if we have to and remove trailing slash
    }
  }
  return null;
}

export function injectTokenInUrl(url: string, token: string | null): string {
  const redirectUrl = new URL(url);
  const allowedHosts = [
    "localhost",
    "10.0.2.2",
    "api.ruumi.io",
    "api.ruumi.rocks",
  ];
  if (allowedHosts.includes(redirectUrl.hostname) && token) {
    redirectUrl.searchParams.delete("grazing_app_token"); // remove previous token if it's there for whatever reason
    redirectUrl.searchParams.append("grazing_app_token", token);
  }
  return redirectUrl.toString();
}

export function redirect() {
  const redirectUrl = getRedirectUrlWithToken();
  if (redirectUrl && !redirectTimeout) {
    redirectTimeout = setTimeout(() => {
      window.location.href = redirectUrl;
    }, 1500);
  }
}
